/**
 * Focus Not Obscured (WCAG 2.2 — SC 2.4.11 AA / 2.4.12 AAA)
 *
 * The site has fixed/sticky UI that stays on screen while the page scrolls
 * (header, CLP header, sticky browse bar, floating CTA bars, scroll-to-top,
 * accessibility launcher). When a keyboard user tabs to an element, the
 * browser only scrolls it into the viewport — it can still land underneath
 * one of these bars.
 *
 * On keyboard focus, this checks whether the focused element is covered by a
 * fixed/sticky element and, if so, scrolls just enough to reveal it fully.
 *
 * Detection is generic (elementFromPoint + nearest fixed/sticky ancestor), so
 * new fixed components are covered without registering them here.
 * Mouse/touch focus is ignored so clicks never cause a page jump.
 */

// Extra space kept between the focused element and the covering bar
const GAP = 8;

// Ignore overlays taller than this share of the viewport (modals, loaders) —
// focus should be inside those, and scrolling the page wouldn't help.
const MAX_BAR_HEIGHT_RATIO = 0.5;

// Handled by focusAboveQuickLinks.js
const QUICK_LINKS_BAR = '.quick__links__inner';

let lastInputWasKeyboard = false;

function isFixedOrSticky(el) {
    const position = window.getComputedStyle(el).position;
    return position === 'fixed' || position === 'sticky';
}

// Nearest fixed/sticky ancestor (or self) of the element painted at a point
function getCoveringBar(el) {
    let node = el;
    while (node && node !== document.body && node !== document.documentElement) {
        if (isFixedOrSticky(node)) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

// Returns the viewport band that is actually visible for the focused element:
// { top, bottom } in viewport px, after excluding covering fixed/sticky bars.
function getUnobscuredBand(target, rect) {
    const viewportHeight = window.innerHeight;
    const band = { top: 0, bottom: viewportHeight };

    const left = Math.max(rect.left, 0);
    const right = Math.min(rect.right, window.innerWidth);
    if (right <= left) {
        return band;
    }

    // Sample across the element's width at its top, middle and bottom edges
    const xs = [left + 1, (left + right) / 2, right - 1];
    const ys = [rect.top + 1, (rect.top + rect.bottom) / 2, rect.bottom - 1];
    const checked = new Set();

    ys.forEach((y) => {
        if (y < 0 || y > viewportHeight) {
            return;
        }
        xs.forEach((x) => {
            const hit = document.elementFromPoint(x, y);
            if (!hit || target.contains(hit) || hit.contains(target)) {
                return;
            }
            const bar = getCoveringBar(hit);
            // The quick-links bar slides in and out, so it is measured here
            // mid-slide; focusAboveQuickLinks.js handles it from its shown
            // position instead (both adjusting it moved focus back under it).
            if (!bar || checked.has(bar) || bar.contains(target) || bar.matches(QUICK_LINKS_BAR)) {
                return;
            }
            checked.add(bar);

            const barRect = bar.getBoundingClientRect();
            if (barRect.height > viewportHeight * MAX_BAR_HEIGHT_RATIO) {
                return;
            }
            // Bars in the upper half of the viewport push the band down,
            // bars in the lower half pull it up.
            if (barRect.top + barRect.height / 2 < viewportHeight / 2) {
                band.top = Math.max(band.top, barRect.bottom);
            } else {
                band.bottom = Math.min(band.bottom, barRect.top);
            }
        });
    });

    return band;
}

function revealFocusedElement(target) {
    if (!target || target === document.body || !target.getBoundingClientRect) {
        return;
    }
    const rect = target.getBoundingClientRect();
    if (!rect.width && !rect.height) {
        return;
    }

    const band = getUnobscuredBand(target, rect);
    let delta = 0;

    if (rect.top < band.top) {
        // Hidden under a top bar (e.g. sticky header) — scroll up
        delta = rect.top - band.top - GAP;
    } else if (rect.bottom > band.bottom) {
        // Hidden under a bottom bar (e.g. floating CTA) — scroll down,
        // but never so far that the top goes under a top bar.
        delta = Math.min(rect.bottom - band.bottom + GAP, rect.top - band.top - GAP);
    }

    if (delta) {
        window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    }
}

function init() {
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' || e.key.indexOf('Arrow') === 0) {
            lastInputWasKeyboard = true;
        }
    }, true);
    document.addEventListener('mousedown', () => { lastInputWasKeyboard = false; }, true);
    document.addEventListener('touchstart', () => { lastInputWasKeyboard = false; }, { capture: true, passive: true });

    document.addEventListener('focusin', (e) => {
        if (!lastInputWasKeyboard) {
            return;
        }
        const target = e.target;
        // Run after the browser's own focus scroll has finished. The site
        // scrolls smoothly, so checking straight away measured the page
        // mid-scroll, and bars that slide in on scroll (help bar) were not
        // on screen yet. Wait for scrollend, or a short fallback when no
        // scroll was needed / scrollend is not supported.
        let done = false;
        const run = () => {
            if (done) {
                return;
            }
            done = true;
            window.removeEventListener('scrollend', run);
            if (document.activeElement === target) {
                revealFocusedElement(target);
            }
        };
        window.addEventListener('scrollend', run);
        window.setTimeout(run, 450);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
